<!-- Register route: path: '/org/:sub_level/:id/unit-evaluation-report', component: UnitEvaluationReport -->
<template>
    <div class="uer-page">
        <div class="section pt-0 mt-0">
            <div class="listed-detail mt-1 page-title">
                <h3 class="text-center mal-text mt-1 custom-title">ശാഖാ മൂല്യനിർണയ റിപ്പോർട്ട്</h3>
                <p class="text-center text-muted small mb-0" v-if="breadcrumb">{{ breadcrumb }}</p>
            </div>
        </div>

        <div class="section text-center py-3" v-if="loading">
            <div class="spinner-border text-primary" role="status"></div>
        </div>

        <div class="section" v-if="!loading && reportList.length === 0">
            <h4 class="text-center text-primary mal-text">ഡാറ്റ ലഭ്യമല്ല</h4>
        </div>

        <!-- District / zone rollup -->
        <div class="section pb-2" v-if="!loading && reportList.length && canDrillDown">
            <div
                class="card uer-card mt-1"
                v-for="row in reportList"
                :key="'rollup-' + row.id"
                @click="goDrillDown(row)"
            >
                <div class="card-body py-3">
                    <div class="d-flex justify-content-between align-items-center">
                        <strong class="mal-text text-primary fs-6">{{ row.name }}</strong>
                        <ion-icon name="chevron-forward-outline" class="text-primary"></ion-icon>
                    </div>
                    <div class="row g-2 mt-2" v-if="row.submitted_count !== undefined">
                        <div class="col-6">
                            <small class="text-muted d-block">സമർപ്പിച്ചത്</small>
                            <span class="fw-semibold">{{ row.submitted_count }} / {{ row.units_count }}</span>
                        </div>
                        <div class="col-6">
                            <small class="text-muted d-block">ശരാശരി മാർക്ക്</small>
                            <span class="fw-semibold">{{ row.avg_score }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Unit list -->
        <div class="section pb-2" v-if="!loading && reportList.length && !canDrillDown">
            <div
                class="card uer-card mt-1"
                v-for="row in reportList"
                :key="'unit-' + row.id"
                :class="{ 'uer-card-active': selectedUnitId === row.id }"
            >
                <div class="card-body py-2" @click="toggleUnitDetail(row)">
                    <div class="d-flex justify-content-between align-items-start">
                        <div>
                            <strong class="mal-text text-primary">{{ row.name }}</strong>
                            <p class="text-danger small mb-0 mal-text" v-if="!row.submitted">സമർപ്പിച്ചിട്ടില്ല</p>
                            <p class="text-muted small mb-0" v-else-if="row.submitted">
                                <span class="uer-cat-badge" :class="'uer-cat-' + (row.category || 'd').toLowerCase()">
                                    {{ row.category }}
                                </span>
                                {{ row.total_score }} മാർക്ക്
                            </p>
                        </div>
                        <ion-icon
                            v-if="row.submitted"
                            :name="selectedUnitId === row.id ? 'chevron-up-outline' : 'chevron-down-outline'"
                            class="text-primary mt-1"
                        ></ion-icon>
                    </div>
                </div>

                <!-- Expanded answers -->
                <div class="uer-detail-panel" v-if="selectedUnitId === row.id && row.submitted">
                    <div class="text-center py-3" v-if="detailLoading">
                        <div class="spinner-border spinner-border-sm text-primary"></div>
                    </div>

                    <div v-if="!detailLoading && unitDetail" class="px-3 pb-3">
                        <div class="uer-score-strip text-center mb-3">
                            <span class="uer-cat-badge large" :class="'uer-cat-' + (unitDetail.score_summary.category || 'd').toLowerCase()">
                                {{ unitDetail.score_summary.category }}
                            </span>
                            <div class="fw-bold text-primary mt-1">{{ unitDetail.score_summary.total_score }} മാർക്ക്</div>
                            <small class="text-muted">{{ unitDetail.score_summary.category_label }}</small>
                        </div>

                        <div class="uer-admin-edit-bar mb-3" @click.stop>
                            <button
                                type="button"
                                class="btn btn-sm btn-block"
                                :class="unitDetail.edit_enabled ? 'btn-warning' : 'btn-success'"
                                :disabled="toggleEditLoading"
                                @click="toggleUnitEdit(selectedUnitId)"
                            >
                                <span v-show="toggleEditLoading" class="spinner-border spinner-border-sm me-1"></span>
                                {{ unitDetail.edit_enabled ? 'എഡിറ്റ് ലോക്ക് ചെയ്യുക' : 'എഡിറ്റ് അനുവദിക്കുക' }}
                            </button>
                        </div>

                        <div class="uer-section" v-for="section in unitDetail.answers_display" :key="section.id">
                            <h6 class="uer-section-title mal-text">
                                <ion-icon :name="section.icon"></ion-icon>
                                {{ section.title }}
                            </h6>
                            <ul class="listview simple-listview flush uer-answer-list">
                                <li v-for="field in section.fields" :key="field.key">
                                    <span class="mal-text">{{ field.label }}</span>
                                    <span class="text-primary fw-medium">{{ field.value }}</span>
                                </li>
                            </ul>
                        </div>

                        <router-link
                            :to="'/org/4/' + row.id + '/unit-evaluation'"
                            class="btn btn-outline-primary btn-sm btn-block mt-2"
                        >
                            പൂർണ്ണ ഫോം കാണുക
                        </router-link>
                    </div>
                </div>
            </div>
        </div>

        <alert-model :type="alert_type" :message="alert_message" ref="alert_model"></alert-model>
    </div>
</template>

<script>
import UserService from "../../services/user.service";
import AlertModel from "../layouts/AlertModel.vue";

export default {
    name: "UnitEvaluationReport",
    components: { AlertModel },
    data() {
        return {
            loading: false,
            detailLoading: false,
            toggleEditLoading: false,
            alert_type: "",
            alert_message: "",
            reportList: [],
            canDrillDown: false,
            userLevel: 1,
            userLevelId: 0,
            selectedUnitId: null,
            unitDetail: null,
            breadcrumb: "",
        };
    },
    computed: {
        currentUser() {
            return this.$store.state.auth.user;
        },
    },
    watch: {
        "$route.params"() {
            this.userLevel = parseInt(this.$route.params.sub_level) || 1;
            this.userLevelId = parseInt(this.$route.params.id) || 0;
            this.selectedUnitId = null;
            this.unitDetail = null;
            this.loadReport();
        },
    },
    mounted() {
        if (!this.currentUser || this.currentUser.user_level != 1) {
            this.alert_type = "danger";
            this.alert_message = "State admin access only";
            this.$refs.alert_model?.show();
            return;
        }
        this.userLevel = parseInt(this.$route.params.sub_level) || 1;
        this.userLevelId = parseInt(this.$route.params.id) || 0;
        this.loadReport();
    },
    methods: {
        goDrillDown(row) {
            this.$router.push("/org/" + (this.userLevel + 1) + "/" + row.id + "/unit-evaluation-report");
        },
        toggleUnitDetail(row) {
            if (!row.submitted) {
                return;
            }
            if (this.selectedUnitId === row.id) {
                this.selectedUnitId = null;
                this.unitDetail = null;
                return;
            }
            this.selectedUnitId = row.id;
            this.loadUnitDetail(row.id);
        },
        loadUnitDetail(unitId) {
            this.detailLoading = true;
            this.unitDetail = null;
            UserService.authGetRequest(`unit-evaluations/${unitId}`).then((res) => {
                this.detailLoading = false;
                if (res.data.status === "success") {
                    const data = res.data.data;
                    this.unitDetail = {
                        unit: data.unit,
                        score_summary: data.score_summary,
                        answers_display: data.answers_display || [],
                        edit_enabled: !!(
                            data.edit_enabled
                            || data.evaluation?.edit_enabled
                            || data.score_summary?.edit_enabled
                        ),
                    };
                    this.breadcrumb = data.unit?.zone?.name
                        ? data.unit.zone.name + " · " + data.unit.name
                        : data.unit?.name || "";
                }
            }).catch(() => {
                this.detailLoading = false;
            });
        },
        toggleUnitEdit(unitId) {
            this.toggleEditLoading = true;
            UserService.authPostRequest(`unit-evaluations/${unitId}/toggle-edit`, {}).then((res) => {
                this.toggleEditLoading = false;
                if (res.data.status === "success") {
                    if (this.unitDetail) {
                        this.unitDetail.edit_enabled = !!res.data.data.edit_enabled;
                        this.unitDetail.is_editable = !!res.data.data.is_editable;
                    }
                    this.alert_type = "success";
                    this.alert_message = res.data.message + " ശാഖാ അഡ്മിൻ ഫോം വീണ്ടും തുറക്കണം.";
                    this.$refs.alert_model?.show();
                } else {
                    this.alert_type = "danger";
                    this.alert_message = res.data.message || "അപ്ഡേറ്റ് ചെയ്യാൻ കഴിഞ്ഞില്ല";
                    this.$refs.alert_model?.show();
                }
            }).catch((err) => {
                this.toggleEditLoading = false;
                this.alert_type = "danger";
                this.alert_message = err.response?.data?.message || "അപ്ഡേറ്റ് ചെയ്യാൻ കഴിഞ്ഞില്ല";
                this.$refs.alert_model?.show();
            });
        },
        loadReport() {
            this.loading = true;
            this.breadcrumb = "";
            UserService.authPostRequest("unit-evaluations/report", {
                user_level: this.userLevel,
                user_level_id: this.userLevelId,
            }).then((res) => {
                this.loading = false;
                if (res.data.status === "success") {
                    this.reportList = res.data.data.list || [];
                    this.canDrillDown = res.data.data.can_drill_down;
                    this.userLevel = res.data.data.user_level || this.userLevel;
                }
            }).catch(() => {
                this.loading = false;
            });
        },
    },
};
</script>

<style scoped>
.uer-page {
    padding-bottom: 24px;
}

.custom-title {
    font-size: 1.05rem;
    font-weight: 600;
    color: #1a418c;
}

.uer-card {
    border: none;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    overflow: hidden;
    cursor: default;
}

.uer-card:not(.uer-card-active) .card-body:first-child {
    cursor: pointer;
}

.uer-card-active {
    box-shadow: 0 4px 14px rgba(26, 65, 140, 0.15);
    border: 1px solid #c5d4f0;
}

.uer-detail-panel {
    border-top: 1px solid #e8ecf4;
    background: #f8faff;
}

.uer-score-strip {
    padding: 12px;
    background: #fff;
    border-radius: 10px;
    border: 1px solid #e8ecf4;
}

.uer-admin-edit-bar {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px;
    background: #fff;
    border-radius: 10px;
    border: 1px solid #e8ecf4;
}

.uer-edit-status {
    font-size: 0.78rem;
    font-weight: 600;
    text-align: center;
    padding: 6px 10px;
    border-radius: 8px;
}

.uer-edit-on {
    background: #d1e7dd;
    color: #0f5132;
}

.uer-edit-off {
    background: #f8f9fa;
    color: #6c757d;
}

.uer-cat-badge {
    display: inline-block;
    min-width: 28px;
    padding: 2px 10px;
    border-radius: 14px;
    font-size: 0.85rem;
    font-weight: 700;
    color: #fff;
    vertical-align: middle;
}

.uer-cat-badge.large {
    min-width: 40px;
    padding: 4px 14px;
    font-size: 1.1rem;
}

.uer-cat-a { background: #198754; }
.uer-cat-b { background: #1a418c; }
.uer-cat-c { background: #fd7e14; }
.uer-cat-d { background: #dc3545; }

.uer-section {
    margin-bottom: 14px;
}

.uer-section-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.85rem;
    font-weight: 600;
    color: #1a418c;
    margin-bottom: 8px;
}

.uer-answer-list li {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 10px;
    padding: 8px 0;
    border-bottom: 1px solid #eef1f6;
    font-size: 0.82rem;
}

.uer-answer-list li:last-child {
    border-bottom: none;
}

.uer-answer-list li span:first-child {
    flex: 1;
    color: #495057;
    line-height: 1.35;
}

.uer-answer-list li span:last-child {
    flex-shrink: 0;
    text-align: right;
    max-width: 42%;
}
</style>
